import { z } from 'zod';
import { FIELD_TYPE } from '@/features/custom-fields/public';
import type { FieldType } from '@/features/custom-fields/public';

// A branded zod schema is needed (`z.number().refine`) — z.enum only accepts strings, and the
// FieldType union is numeric — so `.refine` alone would type `type` as plain `number`, not
// `FieldType`. `z.custom` keeps the runtime check while inferring the literal union.
import { TEMPLATE_SECTOR } from './types';
import type { TemplateSector } from './types';

const sectorValues = Object.values(TEMPLATE_SECTOR) as [TemplateSector, ...TemplateSector[]];
const sectorEnum = z.enum(sectorValues, {
  error: 'projectTemplates:form.validation.sectorRequired',
});

export const templateMetadataSchema = z.object({
  title: z
    .string()
    .min(1, { error: 'projectTemplates:editor.fields.validation.titleRequired' })
    .max(120, { error: 'projectTemplates:editor.fields.validation.titleTooLong' }),
  sector: sectorEnum,
  description: z
    .string()
    .max(500, { error: 'projectTemplates:editor.fields.validation.descriptionTooLong' })
    .optional(),
});

export type TemplateMetadataFormValues = z.infer<typeof templateMetadataSchema>;

export const saveAsTemplateSchema = templateMetadataSchema;

export type SaveAsTemplateFormValues = z.infer<typeof saveAsTemplateSchema>;

export const statusPopoverSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'projectTemplates:popovers.status.validation.nameRequired' })
    .max(50, { error: 'projectTemplates:popovers.status.validation.nameTooLong' }),
  color: z.string().min(1, { error: 'projectTemplates:popovers.status.validation.colorRequired' }),
  type: z.enum(['Todo', 'InProgress', 'Done'], {
    error: 'projectTemplates:popovers.status.validation.typeRequired',
  }),
});

export type StatusPopoverFormValues = z.infer<typeof statusPopoverSchema>;

export const tagPopoverSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'projectTemplates:popovers.tag.validation.nameRequired' })
    .max(50, { error: 'projectTemplates:popovers.tag.validation.nameTooLong' }),
  color: z.string().min(1, { error: 'projectTemplates:popovers.tag.validation.colorRequired' }),
});

export type TagPopoverFormValues = z.infer<typeof tagPopoverSchema>;

const validFieldTypes = Object.values(FIELD_TYPE) as number[];
const OPTIONS_FIELD_TYPES: FieldType[] = [FIELD_TYPE.Select, FIELD_TYPE.MultiSelect];

/** `options` is required (non-empty) only for Select/MultiSelect — mirrors the backend's `InvalidStructure` rule. */
export const customFieldPopoverSchema = z
  .object({
    name: z
      .string()
      .min(1, { error: 'projectTemplates:popovers.field.validation.nameRequired' })
      .max(80, { error: 'projectTemplates:popovers.field.validation.nameTooLong' }),
    type: z.custom<FieldType>((v) => typeof v === 'number' && validFieldTypes.includes(v), {
      error: 'projectTemplates:popovers.field.validation.typeRequired',
    }),
    options: z.array(z.string().min(1)).optional(),
  })
  .refine(
    (value) => !OPTIONS_FIELD_TYPES.includes(value.type as FieldType) || !!value.options?.length,
    {
      error: 'projectTemplates:popovers.field.validation.optionsRequired',
      path: ['options'],
    },
  );

export type CustomFieldPopoverFormValues = z.infer<typeof customFieldPopoverSchema>;

export const templateTaskSchema = z.object({
  title: z
    .string()
    .min(1, { error: 'projectTemplates:editor.tasks.validation.titleRequired' })
    .max(200, { error: 'projectTemplates:editor.tasks.validation.titleTooLong' }),
  priority: z.enum(['urgent', 'high', 'medium', 'low'], {
    error: 'projectTemplates:editor.tasks.validation.priorityRequired',
  }),
});

export type TemplateTaskFormValues = z.infer<typeof templateTaskSchema>;

export const templateGroupSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'projectTemplates:editor.groups.validation.nameRequired' })
    .max(100, { error: 'projectTemplates:editor.groups.validation.nameTooLong' }),
});

export type TemplateGroupFormValues = z.infer<typeof templateGroupSchema>;
